import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fetchInboxMessages, markMailMessageSeen, markMailMessageUnread } from "@/lib/api/imap";
import {
  getFolderSnapshot,
  markFolderListViewedThisSession,
  patchFolderSnapshotRemoveUids,
  patchFolderSnapshotSeen,
  subscribeFolderSnapshot,
} from "@/lib/mail/inboxFolderBus";
import { syncFolderHeaders } from "@/lib/mail/folderWarmup";
import {
  getPendingSeenValueKey,
  prunePendingSeenMapKeys,
  setPendingSeenEntryKey,
  type PendingSeenEntry,
} from "@/lib/mail/mailSeenPending";
import { scheduleBodyCatchUp } from "@/lib/mail/bodyCatchUp";
import { INBOX_LIST_SIZE } from "@/lib/constants/inbox";
import { createRefreshScheduler } from "@/lib/mail/mailRefreshSchedule";
import {
  mergeUnifiedInboxMessages,
  tagMessageForAccount,
  unifiedMessageKey,
  type UnifiedMailMessage,
} from "@/lib/unified/unifiedInboxMerge";
import type { MailAccountSummary } from "@/lib/types/mail";

async function loadAccountInbox(
  accountId: number,
  options?: { refresh?: boolean }
): Promise<UnifiedMailMessage[]> {
  const refresh = options?.refresh === true;

  if (!refresh) {
    const snap = getFolderSnapshot(accountId, "INBOX");
    if (snap?.messages.length) {
      return snap.messages.map((m) => tagMessageForAccount(accountId, "INBOX", m));
    }
  }

  const res = await fetchInboxMessages({
    accountId,
    folder: "INBOX",
    limit: INBOX_LIST_SIZE,
    offset: 0,
    refresh,
  });

  if (!res.ok) {
    const snap = getFolderSnapshot(accountId, "INBOX");
    if (snap?.messages.length) {
      return snap.messages.map((m) => tagMessageForAccount(accountId, "INBOX", m));
    }
    return [];
  }

  return (res.data.messages ?? []).map((m) => tagMessageForAccount(accountId, "INBOX", m));
}

export function useUnifiedInbox(accounts: MailAccountSummary[], enabled: boolean) {
  const [messages, setMessages] = useState<UnifiedMailMessage[]>([]);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fromCache, setFromCache] = useState(false);
  const genRef = useRef(0);
  const remergeSchedulerRef = useRef(createRefreshScheduler(280));
  /** Optimisme lu/non lu unifié — clé accountId:uid (évite retour non-lu après mark-seen OK). */
  const pendingSeenRef = useRef(new Map<string, PendingSeenEntry>());
  const accountIds = useMemo(
    () => accounts.map((a) => a.id).sort((a, b) => a - b).join(","),
    [accounts]
  );

  const applyUnifiedPendingSeen = useCallback((list: UnifiedMailMessage[]) => {
    prunePendingSeenMapKeys(pendingSeenRef.current);
    const pending = pendingSeenRef.current;
    if (pending.size === 0) return list;
    let changed = false;
    const next = list.map((m) => {
      const key = unifiedMessageKey(m.accountId, m.uid);
      const seen = getPendingSeenValueKey(pending, key);
      if (seen === undefined || m.seen === seen) return m;
      changed = true;
      return { ...m, seen };
    });
    // Pas de re-tri ici — l’ordre unifié reste stable (comme mono-compte après settle).
    return changed ? next : list;
  }, []);

  const remergeFromBus = useCallback(() => {
    if (!enabled || accounts.length === 0) return;

    const batches = accounts.map((a) => {
      const snap = getFolderSnapshot(a.id, "INBOX");
      if (snap?.messages.length) {
        return snap.messages.map((m) => tagMessageForAccount(a.id, "INBOX", m));
      }
      return [];
    });

    setMessages((prev) =>
      applyUnifiedPendingSeen(mergeUnifiedInboxMessages(batches, prev))
    );
    setFromCache(batches.some((batch) => batch.length > 0));
  }, [accounts, applyUnifiedPendingSeen, enabled]);

  const reload = useCallback(
    async (options?: { silent?: boolean; refresh?: boolean; fromBus?: boolean }) => {
      if (!enabled || accounts.length === 0) {
        setMessages([]);
        setError(null);
        return;
      }

      if (options?.fromBus === true) {
        const silent = options.silent === true;
        if (!silent) setSyncing(true);
        remergeFromBus();
        if (!silent) setSyncing(false);
        return;
      }

      const gen = ++genRef.current;
      const silent = options?.silent === true;
      const refresh = options?.refresh === true;
      if (!silent) setSyncing(true);
      setError(null);

      try {
        const batches = await Promise.all(
          accounts.map((a) => loadAccountInbox(a.id, { refresh }))
        );
        if (gen !== genRef.current) return;

        setMessages((prev) =>
          applyUnifiedPendingSeen(mergeUnifiedInboxMessages(batches, prev))
        );
        setFromCache(batches.some((batch) => batch.length > 0));
      } catch {
        if (gen !== genRef.current) return;
        setError("Impossible de charger la réception unifiée.");
      } finally {
        if (gen === genRef.current && !silent) {
          setSyncing(false);
        }
      }
    },
    [accounts, applyUnifiedPendingSeen, enabled, remergeFromBus]
  );

  /** Bouton ↻ liste — sync IMAP tous comptes puis refusion (équivalent pullInboxNow mono-compte). */
  const pullInboxNow = useCallback(
    async (options?: { skipHeaderSync?: boolean }) => {
      if (!enabled || accounts.length === 0) return;

      const gen = ++genRef.current;
      setSyncing(true);
      setError(null);

      try {
        if (options?.skipHeaderSync !== true) {
          for (const a of accounts) {
            await syncFolderHeaders(a.id, "INBOX");
            if (gen !== genRef.current) return;
          }
        }

        const batches = await Promise.all(
          accounts.map((a) => loadAccountInbox(a.id, { refresh: true }))
        );
        if (gen !== genRef.current) return;

        setMessages((prev) =>
          applyUnifiedPendingSeen(mergeUnifiedInboxMessages(batches, prev))
        );
        setFromCache(batches.some((batch) => batch.length > 0));
      } catch {
        if (gen !== genRef.current) return;
        setError("Impossible de synchroniser la réception unifiée.");
      } finally {
        if (gen === genRef.current) {
          setSyncing(false);
        }
      }
    },
    [accounts, applyUnifiedPendingSeen, enabled]
  );

  useEffect(() => {
    if (!enabled) {
      setMessages([]);
      setSyncing(false);
      setError(null);
      return;
    }
    void reload();
  }, [accountIds, enabled, reload]);

  useEffect(() => {
    if (!enabled || accounts.length === 0) return;

    const scheduleRemerge = () => {
      remergeSchedulerRef.current.schedule("unified-inbox", () => {
        remergeFromBus();
      });
    };

    const unsubs = accounts.map((acc) =>
      subscribeFolderSnapshot(acc.id, "INBOX", scheduleRemerge)
    );

    return () => {
      unsubs.forEach((unsub) => unsub());
      remergeSchedulerRef.current.cancelAll();
    };
  }, [accountIds, accounts, enabled, remergeFromBus]);

  useEffect(() => {
    if (!enabled || accounts.length === 0 || messages.length === 0) return;

    for (const acc of accounts) {
      const batch = messages.filter((m) => m.accountId === acc.id && m.uid > 0);
      if (batch.length > 0) {
        scheduleBodyCatchUp(acc.id, "INBOX", batch);
      }
    }
  }, [accountIds, accounts, enabled, messages.length]);

  const setMessagesSeen = useCallback(
    (
      uids: number[],
      seen: boolean,
      options?: { force?: boolean; accountResolver?: (uid: number) => number | undefined }
    ) => {
      if (uids.length === 0) return;
      const accountIdForUid =
        options?.accountResolver ??
        ((_uid: number) => undefined as number | undefined);

      for (const uid of uids) {
        const accountId = accountIdForUid(uid);
        if (accountId == null) continue;
        setPendingSeenEntryKey(
          pendingSeenRef.current,
          unifiedMessageKey(accountId, uid),
          seen
        );
      }

      setMessages((current) => {
        const targetKeys = new Set<string>();
        for (const uid of uids) {
          const accountId = accountIdForUid(uid);
          if (accountId != null) {
            targetKeys.add(unifiedMessageKey(accountId, uid));
          }
        }

        const next = current.map((m) => {
          const key = unifiedMessageKey(m.accountId, m.uid);
          if (targetKeys.has(key) && m.seen !== seen) {
            return { ...m, seen };
          }
          return m;
        });
        // Pas de re-tri : évite le saut ; le pending tient le flag.
        return next;
      });

      const byAccount = new Map<number, number[]>();
      for (const uid of uids) {
        const accountId = accountIdForUid(uid);
        if (accountId == null) continue;
        const list = byAccount.get(accountId) ?? [];
        list.push(uid);
        byAccount.set(accountId, list);
      }

      for (const [accountId, folderUids] of byAccount) {
        const unique = [...new Set(folderUids)];
        patchFolderSnapshotSeen(accountId, "INBOX", unique, seen);
        markFolderListViewedThisSession(accountId, "INBOX");
        const apiCall = seen
          ? markMailMessageSeen({ accountId, folder: "INBOX", uids: unique })
          : markMailMessageUnread({ accountId, folder: "INBOX", uids: unique });
        void apiCall.then((res) => {
          if (res.ok) {
            // Confirmer pending seulement quand le snapshot IMAP aura le même flag ;
            // en cas d’échec, on annule l’optimisme.
            return;
          }
          for (const uid of unique) {
            pendingSeenRef.current.delete(unifiedMessageKey(accountId, uid));
          }
          setMessages((current) =>
            applyUnifiedPendingSeen(
              current.map((m) => {
                if (m.accountId !== accountId || !unique.includes(m.uid)) return m;
                return { ...m, seen: !seen };
              })
            )
          );
        });
      }
    },
    [applyUnifiedPendingSeen]
  );

  const removeMessages = useCallback(
    (uids: number[], options?: { accountResolver?: (uid: number) => number | undefined }) => {
      if (uids.length === 0) return;
      const resolver = options?.accountResolver;
      const byAccount = new Map<number, number[]>();
      const drop = new Set<string>();

      for (const uid of uids) {
        const accountId = resolver?.(uid);
        if (accountId == null) continue;
        drop.add(unifiedMessageKey(accountId, uid));
        const list = byAccount.get(accountId) ?? [];
        list.push(uid);
        byAccount.set(accountId, list);
      }
      if (drop.size === 0) return;

      for (const [accountId, folderUids] of byAccount) {
        patchFolderSnapshotRemoveUids(accountId, "INBOX", [...new Set(folderUids)]);
      }

      setMessages((prev) =>
        prev.filter((m) => !drop.has(unifiedMessageKey(m.accountId, m.uid)))
      );
    },
    []
  );

  const unreadCount = useMemo(() => messages.filter((m) => !m.seen).length, [messages]);

  return {
    messages,
    total: messages.length,
    syncing,
    loadingInitial: syncing && messages.length === 0,
    error,
    fromCache,
    hasMore: false,
    unreadCount,
    reload,
    pullInboxNow,
    pullNow: pullInboxNow,
    setMessagesSeen,
    removeMessages,
    markMessageSeen: (uid: number, options?: { force?: boolean; accountResolver?: (u: number) => number | undefined }) => {
      setMessagesSeen([uid], true, options);
    },
    markMessageUnread: (uid: number, options?: { force?: boolean; accountResolver?: (u: number) => number | undefined }) => {
      setMessagesSeen([uid], false, options);
    },
  };
}
